import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "Which India team model is right for my business?",
    a: "It depends on the roles, the level of dedication you need, and how strategic the India presence will be. Many teams start with EOR or an ODC. Others begin with a Centre of Excellence or plan for a GCC from day one. We'll discuss the options in the context of your stage and goals.",
  },
  {
    q: "Do we have to move through EOR, ODC, CoE, and GCC in order?",
    a: "No. These are flexible options, not a mandatory sequence. You can start with any model that fits your current needs and change as your team and priorities evolve.",
  },
  {
    q: "Who leads the India team's work and priorities?",
    a: "You do. You define role priorities, hiring decisions, work, quality standards, culture, and strategic direction. Expante provides the local employment and operational foundation so your team can work as part of your business.",
  },
  {
    q: "Who owns the intellectual property created by the India team?",
    a: "You retain 100% IP ownership. Every employee signs an IP assignment clause in their contract that transfers all work product, code, designs, and inventions directly to your company. We never claim any IP rights.",
  },
  {
    q: "How long does it take to set up an India team?",
    a: "Most India teams go live in 2–8 weeks. EOR is fastest at 2–4 weeks because we use an existing legal entity. An ODC typically takes 4–6 weeks, and a full Centre of Excellence 6–8 weeks.",
  },
  {
    q: "What is included in the EOR service?",
    a: "EOR includes hiring and onboarding, payroll processing, statutory benefits (PF, ESI, gratuity), health insurance, equipment provisioning, IT and security infrastructure, HR support, compliance with Indian labor law, and monthly reporting. You only manage day-to-day work.",
  },
  {
    q: "Can we move EOR employees to our own legal entity later?",
    a: "Yes. Most clients start with EOR for speed, then convert employees to their own India entity once they reach 20–50 hires. The transition is supported at no penalty, including transferring contracts, PF accounts, and statutory liabilities.",
  },
  {
    q: "Which Indian cities do you hire in?",
    a: "We hire across Chennai, Bangalore, Hyderabad, Pune, Mumbai, Delhi-NCR, Kochi, and Coimbatore - and support remote-first roles anywhere in India. City choice depends on the talent profile: Bangalore for senior software, Chennai for fintech and embedded, Pune for product engineering.",
  },
];

export default function Faq() {
  return (
    <section id="faq" className="relative -mt-8 rounded-t-[48px] py-20 sm:py-28 lg:py-32 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-14"
        >
          <span className="inline-flex items-center rounded-full border border-[#5B4199]/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B4199] mb-6">
            FAQ
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-7xl font-extrabold tracking-tight text-[#5B4199] leading-[1.04]">
            Frequently asked questions
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#475569]">
            Quick answers to the questions we hear most often.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                className="rounded-[24px] bg-[#F6F3FE] px-7 data-[state=open]:bg-white data-[state=open]:shadow-[0_24px_56px_-20px_rgba(76,29,149,0.28)] transition-all duration-300"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-[15px] sm:text-base font-bold text-[#0B132B] hover:text-[#5B4199] hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm sm:text-[15px] text-[#475569] leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
